const BUSINESS_ID_KEY = 'seaweeds.selectedBusinessId';
const PORTAL_ROLE_KEY = 'seaweeds.portalRole';
const RETURN_URL_KEY = 'seaweeds.returnUrl';
const PORTAL_TOKEN_KEY = 'seaweeds.portalToken';
const HIDE_BACK_ACTION_KEY = 'seaweeds.hideBackAction';
const DEFAULT_ACCOUNTANT_SEAWEED_ACCESS_TABS = ['releasing', 'expenses'];
const ACCOUNTANT_ROUTE_ACCESS_ALIASES = {
  supplier: ['supplier', 'manage-suppliers'],
  lansa: [
    'lansa',
    'lansa-name',
    'seaweed-type',
    'warehouse-location',
    'tare-amount-fee',
    'savings-per-kilo',
  ],
};
const CONTEXT_STORAGE_KEYS = [
  BUSINESS_ID_KEY,
  PORTAL_ROLE_KEY,
  RETURN_URL_KEY,
  PORTAL_TOKEN_KEY,
  HIDE_BACK_ACTION_KEY,
];

export function initializeBusinessContextFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const businessId = params.get('businessId');
  const portalRole = params.get('portalRole');
  const returnUrl = params.get('returnUrl');
  const portalToken = params.get('portalToken');
  const hideBackAction = params.get('hideBackAction');
  const hasPortalLaunchParams = Boolean(businessId || portalRole || returnUrl || portalToken);

  if (businessId) {
    setContextValue(BUSINESS_ID_KEY, businessId);
  }

  if (portalRole) {
    setContextValue(PORTAL_ROLE_KEY, portalRole);
  }

  if (returnUrl) {
    setContextValue(RETURN_URL_KEY, returnUrl);
  }

  if (portalToken) {
    setContextValue(PORTAL_TOKEN_KEY, portalToken);
  }

  if (hideBackAction) {
    setContextValue(HIDE_BACK_ACTION_KEY, hideBackAction);
  } else if (hasPortalLaunchParams) {
    removeContextValue(HIDE_BACK_ACTION_KEY);
  }

  return {
    businessId: getSelectedBusinessId(),
    portalRole: getPortalRole(),
    returnUrl: getPortalReturnUrl(),
    portalToken: getPortalToken(),
    hideBackAction: isPortalBackActionHidden(),
  };
}

export function getSelectedBusinessId() {
  return getContextValue(BUSINESS_ID_KEY);
}

export function getPortalRole() {
  return getContextValue(PORTAL_ROLE_KEY) || getCurrentUrlParam('portalRole');
}

export function hasPortalSession() {
  return Boolean(getSelectedBusinessId() && getPortalRole() && getPortalToken());
}

export function setPortalSessionContext({
  businessId,
  portalRole,
  returnUrl,
  portalToken,
  hideBackAction = false,
}) {
  if (businessId) {
    setContextValue(BUSINESS_ID_KEY, businessId);
  }

  if (portalRole) {
    setContextValue(PORTAL_ROLE_KEY, portalRole);
  }

  if (returnUrl) {
    setContextValue(RETURN_URL_KEY, returnUrl);
  }

  if (portalToken) {
    setContextValue(PORTAL_TOKEN_KEY, portalToken);
  }

  if (hideBackAction) {
    setContextValue(HIDE_BACK_ACTION_KEY, '1');
  } else {
    removeContextValue(HIDE_BACK_ACTION_KEY);
  }
}

export function isRouteAllowedForPortalRole(routeRole) {
  if (!hasPortalSession()) {
    return false;
  }

  const portalRole = getPortalRole();
  const route = typeof routeRole === 'string' ? { role: routeRole } : routeRole;

  if (portalRole === 'admin-custom') {
    return route.role === 'admin' &&
      ['overview', 'daily-entries', 'initial-summary-po', 'supplier-balancing', 'sales-analytics'].includes(route.id);
  }

  if (portalRole === 'accountant') {
    return route.role === 'admin' && isAccountantRouteAllowed(route.id);
  }

  return portalRole === route.role;
}

function isAccountantRouteAllowed(routeId) {
  const accessTabs = getAccountantSeaweedAccessTabs();
  const routeAccessIds = ACCOUNTANT_ROUTE_ACCESS_ALIASES[routeId] || [routeId];

  return routeAccessIds.some((item) => accessTabs.includes(item));
}

export function getAccountantSeaweedAccessTabs() {
  const tokenAccessTabs = readSeaweedAccessTabsFromPortalToken();

  if (Array.isArray(tokenAccessTabs)) {
    return tokenAccessTabs;
  }

  return DEFAULT_ACCOUNTANT_SEAWEED_ACCESS_TABS;
}

export function getEffectivePortalRole() {
  return getPortalRole();
}

export function isPortalBackActionHidden() {
  return getContextValue(HIDE_BACK_ACTION_KEY) === '1';
}

export function getPortalReturnUrl() {
  return getStoredPortalReturnUrl() || getDefaultPortalReturnUrl();
}

export function getPortalLogoutUrl() {
  const portalRole = getPortalRole();
  const shouldLogoutToStaffLogin =
    portalRole === 'staff' ||
    portalRole === 'accountant' ||
    (portalRole === 'admin-custom' && isPortalBackActionHidden());
  const logoutPath =
    shouldLogoutToStaffLogin
      ? '/login?logout=1'
      : '/admin?logout=1';

  if (shouldLogoutToStaffLogin) {
    return buildStaffPortalUrl(logoutPath);
  }

  return buildPortalReturnUrl(logoutPath, portalRole);
}

export function getPortalReloginUrl(pathname = typeof window === 'undefined' ? '' : window.location.pathname) {
  const portalRole = getPortalRole();
  const isStaffPath = String(pathname || '').startsWith('/staff');
  const shouldReloginToStaffLogin =
    portalRole === 'staff' ||
    portalRole === 'accountant' ||
    (!portalRole && isStaffPath) ||
    (portalRole === 'admin-custom' && isPortalBackActionHidden());
  const loginPath = shouldReloginToStaffLogin ? '/login?logout=1' : '/admin/login?logout=1';

  if (shouldReloginToStaffLogin) {
    return buildStaffPortalUrl(loginPath);
  }

  return buildPortalReturnUrl(loginPath, portalRole);
}

export function getStaffLoginUrl() {
  return buildStaffPortalUrl('/login');
}

export function getPortalToken() {
  return getContextValue(PORTAL_TOKEN_KEY);
}

function readSeaweedAccessTabsFromPortalToken() {
  const token = getPortalToken();

  if (!token) {
    return null;
  }

  try {
    const [, encodedPayload] = token.split('.');
    const base64Payload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const normalizedPayload = base64Payload.padEnd(
      base64Payload.length + ((4 - (base64Payload.length % 4)) % 4),
      '='
    );
    const payload = JSON.parse(window.atob(normalizedPayload));

    return Array.isArray(payload.seaweed_access_tabs) ? payload.seaweed_access_tabs : null;
  } catch (error) {
    return null;
  }
}

export function setPortalToken(token) {
  if (!token) {
    removeContextValue(PORTAL_TOKEN_KEY);
    return;
  }

  setContextValue(PORTAL_TOKEN_KEY, token);
}

export function clearBusinessContext() {
  CONTEXT_STORAGE_KEYS.forEach(removeContextValue);
}

function getDefaultPortalReturnUrl() {
  return buildEPortalUrl('/admin');
}

function buildStaffPortalUrl(path) {
  return buildPortalReturnUrl(path, 'staff');
}

function buildPortalReturnUrl(path, portalRole = '') {
  const returnUrl = getStoredPortalReturnUrl();

  if (returnUrl) {
    try {
      return new URL(path, returnUrl).toString();
    } catch (error) {
      // Fall through to the configured e-Portal URL when the stored return URL is invalid.
    }
  }

  return buildEPortalUrl(path, portalRole);
}

function getStoredPortalReturnUrl() {
  return getContextValue(RETURN_URL_KEY) || getCurrentUrlParam('returnUrl');
}

function buildEPortalUrl(path, portalRole = '') {
  const ePortalUrl = process.env.REACT_APP_E_PORTAL_URL || 'http://localhost:3000';
  const nextUrl = new URL(path, `${ePortalUrl.replace(/\/$/, '')}/`);

  if (
    portalRole === 'staff' ||
    nextUrl.pathname === '/login'
  ) {
    nextUrl.hostname = resolveStaffPortalHostname(nextUrl.hostname);
  }

  return nextUrl.toString();
}

function resolveStaffPortalHostname(hostname) {
  if (isLocalHostname(hostname)) {
    return 'seaweed.localhost';
  }

  if (hostname === 'eportal.rcmventures.net') {
    return 'seaweed.rcmventures.net';
  }

  if (hostname.startsWith('eportal.')) {
    return hostname.replace(/^eportal\./, 'seaweed.');
  }

  return hostname;
}

function isLocalHostname(hostname) {
  return hostname === 'localhost' || hostname.endsWith('.localhost');
}

function getContextValue(key) {
  return getStorageValue(window.sessionStorage, key) || getStorageValue(window.localStorage, key);
}

function getCurrentUrlParam(name) {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return new URLSearchParams(window.location.search).get(name) || '';
  } catch (error) {
    return '';
  }
}

function setContextValue(key, value) {
  setStorageValue(window.sessionStorage, key, value);
  setStorageValue(window.localStorage, key, value);
}

function removeContextValue(key) {
  removeStorageValue(window.sessionStorage, key);
  removeStorageValue(window.localStorage, key);
}

function getStorageValue(storage, key) {
  try {
    return storage.getItem(key) || '';
  } catch (error) {
    return '';
  }
}

function setStorageValue(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch (error) {
    // Ignore private-mode/quota errors; the other storage layer may still work.
  }
}

function removeStorageValue(storage, key) {
  try {
    storage.removeItem(key);
  } catch (error) {
    // Ignore storage access errors.
  }
}
